import React from "react";
import { Check } from "lucide-react";
import { clockStyleColorForInput } from "../../utils/clockWidgetStyle";
import { tr } from "../../../i18n";

/**
 * @param {object} props
 * @param {string} props.variant
 * @param {Record<string, unknown>} props.style merged defaults + overrides
 * @param {(patch: Record<string, unknown>) => void} props.onPatch
 */
export default function ClockWidgetStylePanel({ variant, style, onPatch }) {
  const s = style || {};

  const shadowToggle = (
    <div className="de-rs-row" style={{ alignItems: "center", marginBottom: 8 }}>
      <label className="de-rs-inline-label" style={{ minWidth: 100, fontSize: 12 }} htmlFor="clock-shadow">
        {tr("Shadow")}
      </label>
      <input
        id="clock-shadow"
        type="checkbox"
        checked={s.shadow !== false}
        onChange={(e) => onPatch({ shadow: e.target.checked })}
        style={{ width: 18, height: 18, cursor: "pointer", accentColor: "var(--primary-color)" }}
      />
    </div>
  );

  const rowColor = (label, key) => (
    <div className="de-rs-row" key={key} style={{ alignItems: "center", marginBottom: 8 }}>
      <label className="de-rs-inline-label" style={{ minWidth: 100, fontSize: 12 }}>
        {label}
      </label>
      <input
        type="color"
        value={clockStyleColorForInput(s[key], "#ffffff")}
        onChange={(e) => onPatch({ [key]: e.target.value })}
        style={{ width: 36, height: 28, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
      />
    </div>
  );

  if (variant === "gradient-night") {
    const grad = s.cardMode !== "solid";
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">{tr("Clock appearance")}</span>
        <div className="de-rs-scale-mode" style={{ marginBottom: 10 }}>
          <button
            type="button"
            className={`de-rs-scale-btn ${grad ? "active" : ""}`}
            onClick={() => onPatch({ cardMode: "gradient" })}
          >
            {grad && <Check size={12} />} {tr("Gradient")}
          </button>
          <button
            type="button"
            className={`de-rs-scale-btn ${!grad ? "active" : ""}`}
            onClick={() => onPatch({ cardMode: "solid" })}
          >
            {!grad && <Check size={12} />} {tr("Solid")}
          </button>
        </div>
        {grad ? (
          <>
            {rowColor("Gradient A", "gradientColorLeft")}
            {rowColor("Gradient B", "gradientColorRight")}
          </>
        ) : (
          rowColor("Card", "cardSolid")
        )}
        {rowColor("Time", "timeColor")}
        {rowColor("AM/PM", "subColor")}
        {rowColor("Date", "dateColor")}
        {rowColor("Moon", "moonColor")}
        {shadowToggle}
      </div>
    );
  }

  if (variant === "liquid-crystal") {
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">{tr("Clock appearance")}</span>
        {rowColor("Background", "lcdBg")}
        {rowColor("Digits", "lcdText")}
        {shadowToggle}
      </div>
    );
  }

  if (variant === "longform-analog") {
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">{tr("Clock appearance")}</span>
        {rowColor("Face gradient A", "faceGradientA")}
        {rowColor("Face gradient B", "faceGradientB")}
        {rowColor("Outer ring", "faceStroke")}
        {rowColor("Hands and ring", "handColor")}
        {rowColor("Hour text", "hourTextColor")}
        {rowColor("Minute text", "minuteTextColor")}
        {rowColor("Calendar text", "calendarTextColor")}
        {rowColor("Dial fill", "dialFill")}
        {rowColor("Dial stroke", "dialStroke")}
        {shadowToggle}
      </div>
    );
  }

  if (variant === "neumorphic-analog") {
    return (
      <div className="de-rs-section">
        <span className="de-rs-section-label">{tr("Clock appearance")}</span>
        {rowColor("Frame", "frameBg")}
        {rowColor("Face outer", "faceOuter")}
        {rowColor("Face inner", "faceInner")}
        {rowColor("Inner ring", "faceInnerStroke")}
        {rowColor("Tick fill", "tickFill")}
        {rowColor("Tick stroke", "tickStroke")}
        {rowColor("Numbers", "digitColor")}
        <div className="de-rs-row" style={{ marginBottom: 8 }}>
          <div className="de-rs-field" style={{ flex: 1 }}>
            <label>{tr("Number size")}</label>
            <input
              type="number"
              min={10}
              max={40}
              value={Number(s.digitFontSize) || 24}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isFinite(v)) return;
                onPatch({ digitFontSize: Math.min(40, Math.max(10, v)) });
              }}
            />
          </div>
        </div>
        {rowColor("Hour hand", "handHour")}
        {rowColor("Minute hand", "handMinute")}
        {rowColor("Second hand", "handSecond")}
        {rowColor("Center cap", "pinOuter")}
        {rowColor("Center dot", "pinInner")}
        {shadowToggle}
      </div>
    );
  }

  return null;
}
